import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  File as FileIcon,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileWarning,
} from "lucide-react";

import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";

export const inferTypeFromName = (name: string): string => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "PDF";
  if (ext === "doc" || ext === "docx") return "Word";
  if (ext === "xls" || ext === "xlsx") return "Excel";
  return ext.toUpperCase() || "Datei";
};

export const typeIconFor = (type: string) => {
  const t = (type || "").toUpperCase();
  if (t === "PDF") return <FileText className="h-5 w-5 text-red-600" />;
  if (t === "WORD" || t === "DOC" || t === "DOCX")
    return <FileText className="h-5 w-5 text-blue-600" />;
  if (t === "EXCEL" || t === "XLS" || t === "XLSX")
    return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
  if (t === "PNG" || t === "JPG" || t === "JPEG" || t === "SVG" || t === "GIF")
    return <FileImage className="h-5 w-5 text-purple-600" />;
  return <FileIcon className="h-5 w-5 text-muted-foreground" />;
};

export const formatBytes = (bytes?: number): string => {
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

interface PdfPreviewProps {
  name: string;
  /** Local object URL for not-yet-uploaded documents. */
  previewUrl?: string;
  /** Server path for persisted documents, fetched through authenticatedFetch. */
  url?: string;
}

/** A small PDF thumbnail that supports local previews and authenticated files. */
export function PdfPreview({ name, previewUrl, url }: PdfPreviewProps) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | undefined>(previewUrl);
  // A failed fetch used to leave the "…" placeholder up forever, indistinguishable
  // from one that is still loading — which is how a 403 on every building document
  // (tri-server #732) looked like a slow thumbnail rather than a refused one.
  const [failed, setFailed] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (previewUrl) {
      setSrc(previewUrl);
      setFailed(undefined);
      return;
    }
    if (!url) {
      setSrc(undefined);
      setFailed(undefined);
      return;
    }

    let cancelled = false;
    let createdBlobUrl: string | undefined;
    setSrc(undefined);
    setFailed(undefined);

    (async () => {
      try {
        const response = await authenticatedFetch(
          `${configuration.serverUrl}${url}`,
          { method: "GET", headers: { Accept: "*/*" } },
        );
        if (!response.ok) {
          if (!cancelled) setFailed(String(response.status));
          return;
        }
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" }),
        );
        createdBlobUrl = objectUrl;
        if (!cancelled) setSrc(objectUrl);
      } catch {
        if (!cancelled) setFailed("");
      }
    })();

    return () => {
      cancelled = true;
      if (createdBlobUrl) {
        try {
          URL.revokeObjectURL(createdBlobUrl);
        } catch {
          /* noop */
        }
      }
    };
  }, [previewUrl, url]);

  if (!src) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-1 rounded-md border bg-muted p-1 text-center text-xs text-muted-foreground"
        style={{ width: 100, height: 140 }}
        title={
          failed
            ? t("documentPreview.unavailableWithStatus", { status: failed })
            : undefined
        }
      >
        {failed === undefined ? (
          "…"
        ) : (
          <>
            <FileWarning className="h-5 w-5" />
            <span>{t("documentPreview.unavailable")}</span>
            {failed ? <span className="opacity-70">{failed}</span> : null}
          </>
        )}
      </div>
    );
  }

  return (
    <iframe
      title={`preview-${name}`}
      src={`${src}#toolbar=0&navpanes=0&view=FitH`}
      className="rounded-md border"
      style={{ width: 100, height: 140 }}
    />
  );
}
